import { useMemo } from "react";
import { formatAge } from "../alert-badge";
import { summaryOf } from "../lib/alerts";
import type { SupervisedCall } from "../types";

const ORDER: Record<string, number> = { red: 0, amber: 1, green: 2 };

function fmtElapsed(since?: string) {
  if (!since) return "--:--";
  const s = Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 1000));
  return formatAge(s);
}

export function AlertQueue({
  calls,
  selectedCallId,
  onSelect,
}: {
  calls: SupervisedCall[];
  selectedCallId: string | null;
  onSelect: (callId: string) => void;
}) {
  const sorted = useMemo(() => {
    return [...calls].sort((a, b) => {
      const tierA = a.risk?.level ?? (a.severity === "critical" ? "red" : a.severity === "warn" ? "amber" : "green");
      const tierB = b.risk?.level ?? (b.severity === "critical" ? "red" : b.severity === "warn" ? "amber" : "green");
      const d = (ORDER[tierA] ?? 2) - (ORDER[tierB] ?? 2);
      if (d !== 0) return d;
      const scoreA = a.risk?.score ?? (tierA === "red" ? 75 : tierA === "amber" ? 45 : 0);
      const scoreB = b.risk?.score ?? (tierB === "red" ? 75 : tierB === "amber" ? 45 : 0);
      if (scoreA !== scoreB) return scoreB - scoreA;
      return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
    });
  }, [calls]);

  if (!sorted.length) {
    return (
      <div className="rounded-xl border border-dashed border-border p-10 text-center font-mono">
        <p className="font-medium text-foreground text-xs">NO LIVE CALLS</p>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Calls appear here automatically as batches dial out.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto rounded-xl border border-border bg-card" data-testid="supervision-alert-queue">
      <table className="w-full text-[11px] font-mono">
        <thead className="sticky top-0 bg-card border-b border-border z-10">
          <tr className="text-left text-[9px] tracking-widest text-muted-foreground uppercase">
            <th className="px-3 py-2">TIER</th>
            <th className="px-3 py-2">SCORE</th>
            <th className="px-3 py-2">ELAPSED</th>
            <th className="px-3 py-2">CUSTOMER</th>
            <th className="px-3 py-2">CAMPAIGN</th>
            <th className="px-3 py-2">LANG</th>
            <th className="px-3 py-2">STATE</th>
            <th className="px-3 py-2">TOP TRIGGER</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((c) => {
            const anyCall = c as any;
            const summary = summaryOf(c);
            const rawTier = c.risk?.level ?? (c.severity === "critical" ? "red" : c.severity === "warn" ? "amber" : "green");
            const tier = rawTier === "red" || rawTier === "amber" || rawTier === "green" ? rawTier : "green";
            const score = c.risk?.score ?? (tier === "red" ? 75 : tier === "amber" ? 45 : 12);
            const topRule =
              summary.top?.detail ||
              summary.top?.type ||
              (anyCall.risk_reasons?.length ? anyCall.risk_reasons[anyCall.risk_reasons.length - 1] : "—");
            const isHeld = Boolean(c.holder || anyCall.state === "human_controlled");
            const isListening = anyCall.state === "supervisor_listening";
            const state = isHeld ? "HUMAN" : isListening ? "LISTENING" : anyCall.state ? anyCall.state.toUpperCase() : "LIVE";

            return (
              <tr
                key={c.call_id}
                data-testid={`queue-row-${c.call_id}`}
                onClick={() => onSelect(c.call_id)}
                className={`border-b border-border cursor-pointer transition-colors ${
                  c.call_id === selectedCallId
                    ? "bg-primary/10"
                    : tier === "red"
                    ? "bg-risk-red/10 hover:bg-risk-red/20"
                    : tier === "amber"
                    ? "bg-risk-amber/5 hover:bg-risk-amber/15"
                    : "hover:bg-muted/40"
                }`}
              >
                <td className="px-3 py-2">
                  <span
                    className={`inline-block w-2 h-2 rounded-full ${
                      tier === "red"
                        ? "bg-risk-red"
                        : tier === "amber"
                        ? "bg-risk-amber"
                        : "bg-risk-green"
                    }`}
                  />
                  <span
                    className={`ml-2 font-bold ${
                      tier === "red"
                        ? "text-risk-red"
                        : tier === "amber"
                        ? "text-risk-amber"
                        : "text-muted-foreground"
                    }`}
                  >
                    {tier.toUpperCase()}
                  </span>
                </td>
                <td className="px-3 py-2 tnum font-semibold">{score}</td>
                <td className="px-3 py-2 tnum text-muted-foreground">{fmtElapsed(c.created_at || anyCall.started_at)}</td>
                <td className="px-3 py-2 font-medium">{c.contact_name || c.customer_name || anyCall.customer_ref || "Caller"}</td>
                <td className="px-3 py-2 text-muted-foreground">{c.campaign || c.agent_name || anyCall.campaign_name || "General"}</td>
                <td className="px-3 py-2 uppercase text-muted-foreground">{anyCall.language || "hi"}</td>
                <td className="px-3 py-2 text-muted-foreground">{state}</td>
                <td
                  className={`px-3 py-2 font-medium truncate max-w-[220px] ${
                    tier === "red"
                      ? "text-risk-red"
                      : tier === "amber"
                      ? "text-risk-amber"
                      : "text-muted-foreground"
                  }`}
                >
                  {topRule}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
